/**
 * Ingredient autocomplete logic
 */

import {CompletionContext} from "@codemirror/autocomplete";
import {EditorView} from "@codemirror/view";
import {tokensField, setTokensEffect} from "../state/editor.state";
import {getDraftNearCursor} from "../utils/draft.utils";

/**
 * Create ingredient completion source
 */
export function ingredientCompletionSource(
  ingredients: string[],
  log: (msg: string, data?: any) => void
) {
  const MIN_CHARS_OUTSIDE = 2;

  return (ctx: CompletionContext) => {
    const state = ctx.state;
    const tokens = state.field(tokensField);
    const pos = ctx.pos;

    // INSIDE DRAFT MODE
    const draft = getDraftNearCursor(tokens, pos);
    if (draft) {
      const options = ingredients.map((label) => ({
        label,
        apply: (view: EditorView) => {
          const start = draft.from;
          const end = start + label.length;

          // Get current tokens and update the draft to be an ingredient token
          const currentTokens = view.state.field(tokensField);
          const nextTokens = currentTokens.map((t) =>
            (t === draft || (t.from === draft.from && t.type === "ingredient_draft"))
              ? {from: start, to: end, type: "ingredient" as const}
              : t
          );

          // Insert ingredient and add space after it
          view.dispatch({
            changes: {from: draft.from, to: pos, insert: label + " "},
            effects: setTokensEffect.of(nextTokens),
            selection: {anchor: end + 1},
            scrollIntoView: true,
          });

          log("✨ autocomplete (draft)", {label});
        },
      }));

      return {
        from: draft.from,
        to: pos,
        options,
      };
    }

    // OUTSIDE DRAFT MODE
    const before = ctx.matchBefore(/\p{L}[\p{L}\p{N}_\-]*/u);
    if (!before) return null;

    if (!ctx.explicit && before.text.length < MIN_CHARS_OUTSIDE) {
      return null;
    }

    const typed = before.text.toLowerCase();

    const options = ingredients
      .filter((i) => i.toLowerCase().includes(typed))
      .map((label) => ({
        label,
        apply: (view: EditorView) => {
          const start = before.from;
          const end = start + label.length;

          // Insert ingredient and add space after it
          view.dispatch({
            changes: {from: before.from, to: before.to, insert: label + " "},
            effects: setTokensEffect.of(
              view.state.field(tokensField).concat([{from: start, to: end, type: "ingredient"}])
            ),
            selection: {anchor: end + 1},
          });

          log("✨ autocomplete (normal text)", {label});
        },
      }));

    return {
      from: before.from,
      to: before.to,
      options,
    };
  };
}

/**
 * Normalize ingredient list (trim, deduplicate, sort)
 */
export function normalizeIngredientList(raw: string[]): string[] {
  const trimmed = raw.map((s) => s.trim()).filter(Boolean);
  const unique = Array.from(new Set(trimmed));
  return unique.sort((a, b) => a.localeCompare(b, "de", {sensitivity: "base"}));
}
